import { extend, override } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import ItemList from 'flarum/common/utils/ItemList';
import FieldSet from 'flarum/common/components/FieldSet';
import Button from 'flarum/common/components/Button';
import Stream from 'flarum/common/utils/Stream';
import associationsField from './utils/associationsField';
import addProfileFieldItems from './utils/addProfileFieldItems';
import formatDate from './utils/formatDate';
import parseDate from './utils/parseDate';
// SettingsPage is a plain top-level import in core's own extend.ts (not
// chunked), but there's no downside to using the same deferred string form
// used for SignUpModal elsewhere in this extension, and it keeps this file
// consistent with that one rather than mixing conventions.
const SETTINGS_PAGE = 'flarum/forum/components/SettingsPage';

/**
 * Lets a user edit the same five optional fields SignUpModal collects at
 * registration (see addRegistrationFields.js) from their own settings page
 * afterwards - a new "Profil Champis" FieldSet, following the exact
 * pattern flarum/gdpr uses to add its own "Personal Data" section
 * (extend `settingsItems` to inject a FieldSet, define a new
 * `champisProfileItems` method via `override` - there's no original to
 * chain to, override is just being used here as a way to add a brand new
 * method to the prototype).
 *
 * Plus a date of birth (settings only, not at registration): its owner
 * types it, everybody else only sees the age it gives.
 *
 * Saved as one plain `user.save({...})` covering all these fields at once,
 * rather than per-field autosave (like the Switch-based privacy/device
 * settings do) - editing several text fields and a variable-length list
 * fits a single "Enregistrer" action better than saving on every
 * keystroke or blur.
 */
export default function addProfileSettings() {
  extend(SETTINGS_PAGE, 'oninit', function () {
    const user = this.user;
    this.champisFirstName = Stream(user.champisFirstName() || '');
    this.champisLastName = Stream(user.champisLastName() || '');
    this.champisLocation = Stream(user.champisLocation() || '');
    this.champisWebsite = Stream(user.champisWebsite() || '');
    this.champisAssociations = (user.champisAssociations() || []).map(association => ({
      ...association
    }));
    this.champisBirthDate = Stream(formatDate(user.champisBirthDate()) || '');
    this.champisBirthDateInvalid = false;
    this.champisProfileSaving = false;
  });
  extend(SETTINGS_PAGE, 'settingsItems', function (items) {
    items.add('champisProfile', m(FieldSet, {
      className: "Settings-champisProfile FieldSet--form",
      label: app.translator.trans('champis-users.forum.profile_settings.heading')
    }, this.champisProfileItems().toArray()), -50);
  });
  override(SETTINGS_PAGE, 'champisProfileItems', function () {
    const items = new ItemList();
    addProfileFieldItems(items, {
      firstName: this.champisFirstName,
      lastName: this.champisLastName,
      location: this.champisLocation,
      website: this.champisWebsite,
      disabled: this.champisProfileSaving,
      priority: 50,
      step: 10
    });

    // Typed as jj.mm.aaaa (a native date input shows the browser's own
    // language's format, and a calendar is no way to reach 1949 anyway).
    items.add('champisBirthDate', m("div", {
      className: 'Form-group' + (this.champisBirthDateInvalid ? ' Form-group--invalid' : '')
    }, m("label", {
      className: "label"
    }, app.translator.trans('champis-users.forum.profile_fields.birth_date_label')), m("input", {
      className: "FormControl",
      type: "text",
      placeholder: app.translator.trans('champis-users.forum.profile_fields.birth_date_placeholder', {}, true),
      "aria-label": app.translator.trans('champis-users.forum.profile_fields.birth_date_label', {}, true),
      "aria-invalid": this.champisBirthDateInvalid ? 'true' : undefined,
      bidi: this.champisBirthDate,
      disabled: this.champisProfileSaving
    }), m("p", {
      className: "helpText"
    }, app.translator.trans(`champis-users.forum.profile_fields.${this.champisBirthDateInvalid ? 'birth_date_invalid' : 'birth_date_help'}`))), 15);
    items.add('champisAssociations', associationsField(this.champisAssociations, {
      disabled: this.champisProfileSaving
    }), 10);
    items.add('champisSave', m("div", {
      className: "Form-group"
    }, m(Button, {
      className: "Button Button--primary",
      loading: this.champisProfileSaving,
      onclick: () => this.saveChampisProfile()
    }, app.translator.trans('champis-users.forum.profile_settings.save_button'))), -10);
    return items;
  });
  override(SETTINGS_PAGE, 'saveChampisProfile', function () {
    // Empty clears it; otherwise a real day, from 1900 to today (the API's
    // own rule, see extend.php) - or nothing is saved, the field flagged.
    const birthDateText = this.champisBirthDate().trim();
    const birthDate = birthDateText === '' ? null : parseDate(birthDateText);
    const today = new Date();
    const todayIso = [today.getFullYear(), String(today.getMonth() + 1).padStart(2, '0'), String(today.getDate()).padStart(2, '0')].join('-');
    this.champisBirthDateInvalid = birthDateText !== '' && (!birthDate || birthDate < '1900-01-01' || birthDate > todayIso);
    if (this.champisBirthDateInvalid) return;
    this.champisProfileSaving = true;
    const associations = this.champisAssociations.map(association => ({
      name: association.name.trim(),
      url: association.url.trim()
    })).filter(association => association.name || association.url);
    const resetSaving = () => {
      this.champisProfileSaving = false;
      m.redraw();
    };
    this.user.save({
      champisFirstName: this.champisFirstName(),
      champisLastName: this.champisLastName(),
      champisLocation: this.champisLocation(),
      champisWebsite: this.champisWebsite(),
      champisAssociations: associations,
      champisBirthDate: birthDate
    }).then(() => {
      this.champisAssociations = (this.user.champisAssociations() || []).map(association => ({
        ...association
      }));
      this.champisBirthDate(formatDate(this.user.champisBirthDate()) || '');
      resetSaving();
    }, resetSaving);
  });
}